import { type ReactElement, useEffect, useState } from 'react';
import type { IPortfolioDefinitionType } from '../../../../generated/graphql.tsx';
import { defaultRowSpacing } from '../../../StackSpacing.ts';
import {
  calculatePortfolioSelection,
  createParentPortfolioDefinitionAutocompleteOptions,
} from '../PortfolioDefinitionService.tsx';
import gYupResolver from '../../../technical/form/gYupResolver.ts';
import { useFieldArray, useForm, useFormState } from 'react-hook-form';
import GFormProvider from '../../../technical/form/GFormProvider.tsx';
import FormStaticSingleAutocomplete from '../../../technical/form/FormStaticSingleAutocomplete.tsx';
import { Stack } from '@mui/joy';
import RemoveButton from '../../../technical/RemoveButton.tsx';
import ErrorMessage from '../../../technical/errors/ErrorMessage.tsx';
import AddButton from '../../../technical/AddButton.tsx';
import SubmitButton from '../../../technical/form/SubmitButton.tsx';
import FormSelect from '../../../technical/form/FormSelect.tsx';
import WarningMessage from '../../../technical/WarningMessage/WarningMessage.tsx';
import {
  calculateSchema,
  calculateSharedPortfolioDates,
  getPortfolioId,
} from './PortfolioBacktestingForm.validation.ts';
import type { FormOutputFields, FormInputFields } from './PortfolioBacktestingForm.validation.ts';
import { FormDateRangeInput } from 'components/technical/form/FormDateRangeInput.tsx';
import { useGraphQLApiError } from 'components/technical/form/UseGraphQLApiError.tsx';
import isNil from 'lodash/fp/isNil';
import { convertDateInUtcToUTCISODate } from 'components/date.utils.ts';

const calculateDefaultPortfolios = ({
  defaultPortfolioIds,
  portfolioDefOptions,
}: {
  defaultPortfolioIds: string[];
  portfolioDefOptions: {
    options: {
      value: {
        basePortfolioId: string | null;
        id: string;
        name: string;
        extraRebalancingSelection: boolean;
      };
    }[];
  };
}) => {
  const defaultPortfolio: FormInputFields['portfolios'] = [];
  const defaultSelectedOptions = [];
  const portWithoutOptions = [];
  for (const id of defaultPortfolioIds) {
    const matchingOption = portfolioDefOptions.options.find((opt) => opt.value.basePortfolioId === id);
    if (matchingOption) {
      defaultSelectedOptions.push(matchingOption);
      continue;
    }

    portWithoutOptions.push(id);
  }

  defaultPortfolio.push(
    ...defaultSelectedOptions.map((opt) => ({
      portfolio: opt.value,
      synthPortfolioId: null,
    }))
  );

  if (defaultPortfolio.length === 0) {
    defaultPortfolio.push({
      portfolio: null,
      synthPortfolioId: null,
    });
  }

  return { defaultPortfolio, portWithoutOptions };
};

const calculatePortfolioIdForPortfolioFormIndex = (
  updatedPortfolios: FormInputFields['portfolios'],
  portfolios: { id: string }[]
): (FormInputFields['portfolios'][number] & { id: string })[] => {
  const portfolioIndexToValue = new Map<number, FormInputFields['portfolios'][number] & { id: string }>(
    updatedPortfolios.map((port, i) => [
      i,
      {
        ...port,
        id: portfolios[i].id,
      },
    ])
  );

  const finalPortfoliosWithId = updatedPortfolios.map((port, i) => ({
    ...port,
    ...portfolioIndexToValue.get(i)!,
  }));

  return finalPortfoliosWithId;
};

const calculateOutputDateRange = ({
  output,
  portfolioIdToRange,
  onErrorAndThrow,
}: {
  output: FormOutputFields;
  portfolioIdToRange: Map<string, { since: UtcDate | null; to: UtcDate | null }>;
  onErrorAndThrow: (error: Error) => void;
}): { since: UtcDate; to: UtcDate } => {
  const { firstSince, commonTo } = calculateSharedPortfolioDates(
    portfolioIdToRange,
    output.portfolios.map((p) => getPortfolioId(p))
  );

  const since = output.range?.[0] ?? firstSince;
  const to = output.range?.[1] ?? commonTo;

  if (isNil(since)) {
    onErrorAndThrow(new Error('Cannot calculate starting date'));
  }

  if (isNil(to)) {
    onErrorAndThrow(new Error('Cannot calculate ending date'));
  }

  return { since: convertDateInUtcToUTCISODate(since!), to: convertDateInUtcToUTCISODate(to!) };
};

export interface PortfolioBacktestingFormProps {
  definitions: {
    id: string;
    name: string;
    type: IPortfolioDefinitionType;
    genie: boolean;
    dateRange: { since: UtcDate | null; to: UtcDate | null };
    createdFrom?: {
      name: string;
      portfolioDefinition: {
        id: string;
        name: string;
      };
    } | null;
  }[];
  onSubmit: (data: {
    portfolios: { id: string; name: string }[];
    range: { since: UtcDate; to: UtcDate };
  }) => Promise<void>;
  defaultPortfolioIds: string[];
}

const PortfolioBacktestingForm = ({
  definitions,
  onSubmit,
  defaultPortfolioIds,
}: PortfolioBacktestingFormProps): ReactElement => {
  const portfolioIdToRange = new Map<
    string,
    {
      since: UtcDate | null;
      to: UtcDate | null;
    }
  >(definitions.map((def) => [def.id, def.dateRange]));

  const portfolioSelection = calculatePortfolioSelection(definitions);
  const portfolioDefOptions = createParentPortfolioDefinitionAutocompleteOptions(
    Array.from(portfolioSelection.entries()).map(([id, val]) => ({
      id: id,
      name: val.name,
      basePortfolioId: val.basePortfolioId,
      extraRebalancingSelection: val.rebalancingRules.length > 0,
    }))
  );

  const { defaultPortfolio, portWithoutOptions } = calculateDefaultPortfolios({
    defaultPortfolioIds,
    portfolioDefOptions,
  });

  const [someInitialPortfolioHidden, setShowInitialPortfolioHidden] = useState(portWithoutOptions.length > 0);

  const methods = useForm<FormInputFields>({
    resolver: gYupResolver(calculateSchema(portfolioIdToRange)),
    defaultValues: {
      range: null,
      portfolios: defaultPortfolio,
    },
  });

  const { onErrorAndThrow } = useGraphQLApiError(methods);

  const { isSubmitting, isDirty } = methods.formState;
  useEffect(() => {
    if (!isDirty) {
      return;
    }

    setShowInitialPortfolioHidden(false);
  }, [isDirty]);

  const {
    fields: portfolios,
    append,
    remove,
  } = useFieldArray<FormInputFields>({
    control: methods.control,
    name: 'portfolios',
  });

  // field array only rerenders when the array is mutated (adding/removing items), so we need to extra fetch form items
  const updatedPortfolios = methods.watch('portfolios');
  const finalPortfoliosWithId = calculatePortfolioIdForPortfolioFormIndex(updatedPortfolios, portfolios);

  const { errors } = useFormState<FormInputFields>({
    name: 'portfolioLength',
    control: methods.control,
  });

  const portfolioLengthError = errors.portfolioLength?.message;
  return (
    <GFormProvider {...methods}>
      {someInitialPortfolioHidden && (
        <WarningMessage>
          Some initially selected portfolios are hidden. They are not available for backtesting due to lack of data
        </WarningMessage>
      )}
      <form
        onSubmit={methods.handleSubmit(async (data: FormInputFields): Promise<void> => {
          const output = data as FormOutputFields;

          const { since, to } = calculateOutputDateRange({
            output,
            portfolioIdToRange,
            onErrorAndThrow,
          });

          await onSubmit({
            portfolios: output.portfolios.map((port) => ({
              id: getPortfolioId(port),
              name: port.portfolio.name,
            })),
            range: {
              since,
              to,
            },
          });
        })}
      >
        <Stack direction="column" gap={2} alignItems="flex-start">
          <Stack direction="column" gap={defaultRowSpacing}>
            {finalPortfoliosWithId.map((port, index) => (
              <Stack key={port.id} direction="row" gap={1} alignItems="flex-end">
                <FormStaticSingleAutocomplete<FormInputFields>
                  label="Portfolio"
                  width="xl2"
                  name={`portfolios.${index}.portfolio`}
                  {...portfolioDefOptions}
                />
                {port.portfolio?.extraRebalancingSelection && (
                  <FormSelect<FormInputFields>
                    label="Rebalancing rule"
                    width="xl2"
                    name={`portfolios.${index}.synthPortfolioId`}
                    options={(portfolioSelection.get(port.portfolio.id)?.rebalancingRules ?? []).map((rule) => ({
                      label: rule.ruleName,
                      value: rule.portfolioId,
                      key: rule.ruleName,
                    }))}
                  />
                )}
                <RemoveButton
                  disabled={isSubmitting}
                  onClick={() => {
                    remove(index);
                    methods.trigger('portfolioLength');
                  }}
                />
              </Stack>
            ))}
          </Stack>
          {portfolioLengthError && <ErrorMessage>{portfolioLengthError}</ErrorMessage>}
          <AddButton
            onClick={() => {
              append({ portfolio: null, synthPortfolioId: '' });
              methods.trigger('portfolioLength');
            }}
            disabled={isSubmitting}
          >
            Compare
          </AddButton>
          <FormDateRangeInput name="range" label="Date range" width="xl2" allowSelectingOnlySince />
          <SubmitButton>Submit</SubmitButton>
        </Stack>
      </form>
    </GFormProvider>
  );
};

export default PortfolioBacktestingForm;
